// PlayCanvas camera adapter. Implements the renderer-agnostic `ICamera` surface (the
// one input.ts / game.ts depend on) but backs `worldToScreen`/`screenToWorld` with the
// real 3D projection so selection, orders, hover and previews stay pixel-accurate under
// a tilted perspective view. The logical state is a world-space focus point (`x`,`y` in
// sim coords, on the ground plane) plus a `zoom` scalar; `sync()` positions a tilted
// top-down PlayCanvas camera from that state each frame (GDD §7.2 — closer & chunkier
// than an RTS god-view). Pan and zoom are clamped so the battlefield always fills frame.

import * as pc from "playcanvas";
import { clamp, type Vec2 } from "../engine/vec";
import type { ICamera } from "../render/camera";

const PITCH_DEG = 56; // look-down angle from horizontal (GDD §7.2: ~50–60°)
const BASE_DIST = 980; // camera distance at zoom = 1

export class PcCamera implements ICamera {
  x = 1200;
  y = 800;
  zoom = 0.85;
  vw = 1280;
  vh = 720;
  dpr = 1;
  minZoom = 0.5;
  maxZoom = 2.2;

  private app: pc.AppBase;
  private ent: pc.Entity;
  private a = new pc.Vec3();
  private b = new pc.Vec3();
  private out = new pc.Vec3();

  constructor(app: pc.AppBase, ent: pc.Entity) {
    this.app = app;
    this.ent = ent;
  }

  private dist(): number {
    // zoom in (higher zoom) → closer camera. Clamp so the field always fills the frame.
    return clamp(BASE_DIST / this.zoom, BASE_DIST / this.maxZoom, BASE_DIST / this.minZoom);
  }

  /** Position the tilted 3D camera from the logical focus/zoom. Call once per frame. */
  sync(): void {
    const d = this.dist();
    const phi = (PITCH_DEG * Math.PI) / 180;
    const h = d * Math.sin(phi);
    const horiz = d * Math.cos(phi);
    // Camera sits south (+z) of and above the focus, looking down toward it — this
    // orients the view north-up / east-right, matching the 2D renderer's map layout
    // (Legion west on the left, Kindled east on the right).
    this.ent.setPosition(this.x, h, this.y + horiz);
    this.ent.lookAt(this.x, 0, this.y);
    void this.app;
  }

  setViewport(w: number, h: number, dpr: number): void {
    this.vw = w;
    this.vh = h;
    this.dpr = dpr;
  }

  centerOn(p: Vec2): void {
    this.x = p.x;
    this.y = p.y;
  }

  worldToScreen(p: Vec2): Vec2 {
    const r = this.ent.camera!.worldToScreen(this.a.set(p.x, 0, p.y), this.out);
    return { x: r.x, y: r.y };
  }

  screenToWorld(p: Vec2): Vec2 {
    const cam = this.ent.camera!;
    const near = cam.screenToWorld(p.x, p.y, cam.nearClip, this.a);
    const far = cam.screenToWorld(p.x, p.y, cam.farClip, this.b);
    const dy = far.y - near.y;
    if (Math.abs(dy) < 1e-6) return { x: this.x, y: this.y };
    const t = -near.y / dy; // intersect ray with ground plane y = 0
    return { x: near.x + (far.x - near.x) * t, y: near.z + (far.z - near.z) * t };
  }

  panScreen(dx: number, dy: number): void {
    // Convert a screen-pixel delta into a world-space focus shift by unprojecting the
    // viewport center and center+delta onto the ground plane (keeps grab-drag 1:1).
    const cx = this.vw / 2;
    const cy = this.vh / 2;
    const w0 = this.screenToWorld({ x: cx, y: cy });
    const w1 = this.screenToWorld({ x: cx + dx, y: cy + dy });
    this.x += w1.x - w0.x;
    this.y += w1.y - w0.y;
    this.sync();
  }

  zoomAt(screenPt: Vec2, factor: number): void {
    const before = this.screenToWorld(screenPt);
    this.zoom = clamp(this.zoom * factor, this.minZoom, this.maxZoom);
    this.sync();
    const after = this.screenToWorld(screenPt);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.sync();
  }

  clampToMap(mapW: number, mapH: number): void {
    const mx = 120;
    this.x = clamp(this.x, -mx, mapW + mx);
    this.y = clamp(this.y, -mx, mapH + mx);
  }
}
